/**
 * narrowWidth — "this feed is too narrow for 85% bubbles", observed and applied.
 *
 * A bubble takes a share of its container until the container gets small, and then all of it. The
 * share is what keeps a wide pane readable; the switch is what stops a narrow one rendering a
 * column of text three words across with half the width empty beside it.
 *
 * Shared because there are TWO feeds and they are deliberately different components: the thread
 * widget's (common/threadView.js) and Mission Control's meta-thread (missionControl/surfaces/cell/
 * panes.js), which the mission widget owns and keeps separate on purpose. The behaviour is not
 * what makes them different, so it lives here rather than in one of them — it was in one of them,
 * and the other never switched at any width.
 */

/** Below this, a bubble is the whole width. Wide enough that a docked pane still gets the share. */
export const NARROW_WIDTH = 700;

/**
 * Watch `node` and keep `is-narrow` on it in step with its width. Returns a disposer.
 *
 * The toggle lands on the NEXT frame, never inside the observer's own delivery: `is-narrow` changes
 * how bubbles lay out, which resizes the element being observed, which is the loop the browser
 * reports as "ResizeObserver loop completed with undelivered notifications". Nothing is broken when
 * it fires — the deferred notifications arrive next frame — but it surfaces as a window error, and
 * that is enough for the dev server's overlay to throw a red screen over a working app.
 *
 * Coalescing is the other half: a resize settling over several observations within one frame
 * toggles once, and only the last width is used, since the intermediate ones are what it was
 * passing through rather than where it ended.
 *
 * @param {Element|null} node
 * @returns {() => void}
 */
export function observeNarrow(node) {
  if (typeof ResizeObserver === "undefined" || !node) return () => {};
  let frame = null;
  let pending = null;
  let live = true;

  const observer = new ResizeObserver((entries) => {
    pending = entries[entries.length - 1].contentRect.width;
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      const width = pending;
      pending = null;
      if (!live || width == null) return;
      // A zero width is a HIDDEN view — a playground tab that is not showing — not a narrow one.
      if (width <= 0) return;
      node.classList.toggle("is-narrow", width <= NARROW_WIDTH);
    });
  });
  observer.observe(node);

  return () => {
    live = false;
    if (frame) window.cancelAnimationFrame(frame);
    observer.disconnect();
  };
}
